import styles from './miniPlayer.module.css';

/**
 * Where the player's DOM currently lives — the whole of "transitioning between /watch and the
 * minimized player doesn't interrupt playback".
 *
 * The constraint that shapes this file is one line of the HTML spec: **removing a media element from
 * the document pauses it.** Re-parenting it does not, because `appendChild` moves a node in one step
 * with no stable state in between. So the player's box may be moved as often as we like and must
 * never be detached, which rules out every arrangement where React owns its position:
 *
 * - Rendering the surface inside the watch page means React deletes it on navigation. Playback stops.
 * - Portalling it and swapping the portal's container re-creates the node. Playback stops.
 * - Rendering it in two places and hiding one is two `<video>` elements, i.e. two Shaka players.
 *
 * What works is a node React never positions: `host` is a plain `<div>` that React only ever renders
 * *into* (through a portal), and this module `appendChild`s it between the watch page's slot and the
 * minimized frame. React mutates its contents; nothing but `place()` mutates where it is.
 *
 * `frame` is created here rather than rendered, and that is not an aesthetic choice either. If the
 * frame were a React node there would be a commit in which the watch page's slot is already deleted
 * and the frame does not exist yet — React does not order deletions against insertions — and the host
 * would spend that commit detached. A frame that always exists is always somewhere to go, so the
 * hand-off is one `appendChild` with nothing in between. Hidden, it doubles as the parking bay.
 *
 * > ### ⚠️ Construction touches no document, and that is load-bearing under StrictMode
 * >
 * > The first version appended the frame to `document.body` in this function, which a React component
 * > reached through `useMemo`. StrictMode invokes a `useMemo` factory **twice** and runs layout
 * > effects mount → unmount → mount, so two frames were built, one was orphaned in the body forever,
 * > and the *live* dock's `dispose()` had already removed the frame it was still handing the host to.
 * > The player then went to a detached frame the moment the watch page unmounted — i.e. exactly the
 * > detach this file exists to prevent, and it read as "the minimized player never appears".
 * >
 * > So: building elements is pure, `attach`/`detach` are symmetric and idempotent, and every document
 * > mutation happens in an effect that can run twice without meaning anything different.
 */
export interface PlayerDock {
  /** The portal container the surface renders into — always this dock's, never re-created. */
  readonly host: HTMLElement;
  /** Put the frame in the document (an effect, so StrictMode's second mount repeats it harmlessly). */
  attach: (parent: HTMLElement) => void;
  /** Take it out again. Paired with `attach`; the session is ending. */
  detach: () => void;
  /** The watch page's stage, or `null` while no watch page is mounted. Wins over the frame. */
  setSlot: (slot: HTMLElement | null) => void;
  /** Show the minimized frame, or hide it and let it be the parking bay. */
  setMinimized: (minimized: boolean) => void;
  /**
   * Put a swipe-in-progress on the frame, or `null` to let it settle back. See {@link MiniDrag} for
   * why this is four numbers and not a transform.
   */
  follow: (drag: MiniDrag | null) => void;
}

/**
 * A swipe mid-gesture, as the frame should wear it — core's `followSwipe` output with its `action`
 * spent. Four *values*, deliberately not a `transform`: the two shapes spend them differently and
 * only the stylesheet knows which shape is on screen, because only the stylesheet has the media
 * query. A popover grows its own box; the bar grows the picture inside it and leaves its furniture
 * where the reader last saw it. An inline `transform` written from here would be a third opinion
 * about geometry that no media query could overrule.
 */
export interface MiniDrag {
  /** Rightward travel, in CSS pixels. */
  readonly dx: number;
  /** Downward travel, in CSS pixels. */
  readonly dy: number;
  /** A multiplier on whichever dimension the shape grows — ≥ 1. */
  readonly grow: number;
  /** What is left of the box while it is being pushed away — ≤ 1. */
  readonly fade: number;
  /**
   * What releasing **now** would do — core's `decideSwipe` on this same drag. The one value
   * here that is not a geometry: it is what the frame's armed indicator is drawn from, and it comes
   * from core rather than from a second measurement so the ring can never promise an act the release
   * refuses.
   */
  readonly action: 'close' | 'maximize' | null;
}

/**
 * The five properties this file writes, and the whole of its contract with the three stylesheets that
 * read them. Not `--vs-` prefixed on purpose: that namespace is the tokens package's, and a test
 * asserts every `var(--vs-…)` the app reads is defined there. Every consumer declares its own
 * fallback, so a frame nobody has dragged is at rest rather than at nothing.
 *
 * `--mini-snap` is the odd one out. It is a *duration* — how long a change to this box may take — and
 * the rule is one sentence: **the dock's own changes are instant, and the only thing that animates is
 * a box a finger has just let go of.** So it is `0s` while a swipe is being followed (or the box would
 * arrive 120 ms behind the finger), `0s` again whenever `setMinimized` reshapes the frame (or the
 * popover would wipe itself in from `.parked`'s zero width — measured, 0 → 107 → 401 px, dragging a
 * playing video's layout size with it), and absent only for the 120 ms after a release, which is the
 * whole of the snap-back. It has to be a property rather than a class for the same reason: this file
 * assigns `className` wholesale and would wipe one.
 */
const DRAG_PROPERTIES = ['--mini-drag-x', '--mini-drag-y', '--mini-grow', '--mini-fade', '--mini-snap'] as const;

/**
 * The armed indicator's channel, and the reason it is an *attribute* rather than a class or a custom
 * property.
 *
 * A class is out for `--mini-snap`'s reason — this file assigns `className` wholesale — and a custom
 * property cannot be branched on in CSS without a `@property` registration and style queries. An
 * attribute can: it is not hashed by CSS Modules, so `miniPlayer.module.css` matches it on the frame
 * *and* `miniChrome.module.css` could reach into the chrome through it, without either file exporting
 * a global class name.
 *
 * Its value is the **edge the box is heading for** rather than the act, because that is what is drawn
 * and only one of the two can be read off a stylesheet. The act is core's; picking the edge from it is
 * the one line of presentation this file owns: a dismissal moved sideways leaves by the right edge and
 * one moved downward by the bottom, and both are the same accent as the ring. The same attribute
 * dresses both elements — the ring on the frame, and the bar on the window edge below.
 */
const ARMED_ATTRIBUTE = 'miniArmed';

type ArmedEdge = 'top' | 'bottom' | 'right';

function armedEdge(drag: MiniDrag): ArmedEdge | null {
  if (drag.action === 'maximize') return 'top';
  if (drag.action !== 'close') return null;
  return drag.dx > 0 ? 'right' : 'bottom';
}

/**
 * Which of those edges is a **window** edge — i.e. which ones get the bar that is drawn where
 * the box is going rather than on the box itself.
 *
 * Both dismissals do: a push down is aimed off the bottom of the window and a push right off its
 * trailing side, and by the threshold the frame's own edge has left the viewport, so a bar hugging it
 * is a bar nobody can see. `top` does not, and that is the asymmetry rather than an omission — a
 * maximize does not travel anywhere, it opens in place, so the edge it is "heading for" is its own top
 * edge and the window's top edge is a header's width away and about nothing.
 */
function windowEdge(edge: ArmedEdge): 'bottom' | 'right' | null {
  return edge === 'top' ? null : edge;
}

/**
 * Where the window-edge bar starts along that edge, and how long it is. Two properties rather than
 * four because only one axis is ever in question: a bar on the trailing edge is placed and sized
 * vertically, one on the bottom edge horizontally, and the stylesheet knows which from the attribute.
 */
const EDGE_PROPERTIES = ['--mini-edge-offset', '--mini-edge-length'] as const;

/** Sub-pixel travel is not a thing a finger asks for, and three stylesheets round the same numbers. */
const px = (n: number): string => `${String(Math.round(n))}px`;

/** CSS Modules type their exports as possibly-absent; a missing class must not become `"undefined"`. */
const FRAME = styles.frame ?? '';
const PARKED = styles.parked ?? '';
const HOST = styles.host ?? '';
const EDGE = styles.edge ?? '';

/**
 * `appendChild`, or `moveBefore` where the browser has it — and the difference is the YouTube leg.
 * A `<video>` survives `appendChild`, but an `<iframe>` does not: re-inserting one throws its document
 * away and loads it again from `src`. `moveBefore` is the state-keeping move, so the embed goes on
 * playing as it reaches the corner. Without it the embed reloads, and its bridge puts it back where
 * it was (`player/youtubeEmbedBridge.ts`).
 *
 * It throws when either end is not in the document, which `place` can meet while the dock is
 * parked between `detach` and `attach`, and there the plain move is all there is.
 */
function move(parent: HTMLElement, node: HTMLElement): void {
  // `in`, because the DOM types declare it everywhere and older engines do not have it.
  if ('moveBefore' in parent && parent.isConnected && node.isConnected) {
    try {
      parent.moveBefore(node, null);
      return;
    } catch {
      // Falls through to the plain move.
    }
  }
  parent.appendChild(node);
}

export function createPlayerDock(doc: Document): PlayerDock {
  const frame = doc.createElement('div');
  frame.className = PARKED;
  const host = doc.createElement('div');
  host.className = HOST;
  /*
   * The window-edge bar. A second element, and a sibling of the frame rather than anything
   * inside it, because it is the one part of the gesture's feedback that is *not* about where the box
   * is: it stays on the window while the box travels off it. Inside the frame it would be translated
   * away with everything else and clipped by the `overflow: hidden` that rounds the corners.
   *
   * Appended after the frame, which is how it paints over it: both sit at `--vs-z-popover`, so tree
   * order is the tie-break — and it has to win, since a dismissal drags the box across the very strip
   * of window the bar occupies.
   */
  const edge = doc.createElement('div');
  edge.className = EDGE;

  let slot: HTMLElement | null = null;
  let attached = false;

  /*
   * The slot wins whenever it exists, which makes the priority list the whole state machine: a watch
   * page is mounted (play there), or it is not (play in the frame). No flag says which.
   */
  const place = (): void => {
    const parent = slot ?? frame;
    if (host.parentElement !== parent) move(parent, host);
  };

  /**
   * Aim the window-edge bar at the strip of window the box is heading for, and give it the box's own
   * span across that edge — so it starts where the player starts and ends where the player ends, which
   * on the full-width bar shape is the whole edge and on the popover is the height or width of the
   * popover.
   *
   * Measuring mid-drag is exact rather than lucky: **the axis read here is the one the dismissal does
   * not move.** A rightward push translates x and leaves `top`/`height` at rest; a downward one
   * translates y and leaves `left`/`width` at rest. So arming is both the right moment to measure — the
   * freshest possible read of a box whose height only the loaded video knows, and one that costs nothing
   * on a viewport resize because the next gesture measures again — and the last moment it needs
   * measuring, since those numbers stay true for the rest of the gesture. One forced layout per arming,
   * never one per pointer move; the follow itself still touches no geometry it has to read back.
   */
  const aimEdge = (which: 'bottom' | 'right'): void => {
    const box = frame.getBoundingClientRect();
    edge.style.setProperty('--mini-edge-offset', px(which === 'right' ? box.top : box.left));
    edge.style.setProperty('--mini-edge-length', px(which === 'right' ? box.height : box.width));
  };

  /** Both halves of the indicator go together, or the ring and the bar could disagree about the act. */
  const disarm = (): void => {
    delete frame.dataset[ARMED_ATTRIBUTE];
    delete edge.dataset[ARMED_ATTRIBUTE];
    for (const name of EDGE_PROPERTIES) edge.style.removeProperty(name);
  };

  return {
    host,
    attach(parent) {
      if (!attached) {
        parent.appendChild(frame);
        parent.appendChild(edge);
      }
      attached = true;
      place();
    },
    detach() {
      attached = false;
      frame.remove();
      edge.remove();
      host.remove();
    },
    setSlot(next) {
      slot = next;
      place();
    },
    setMinimized(minimized) {
      // Before the class, not after: this reshapes the box from zero-sized to a popover, and the
      // frame's transitions must not take that for something a reader did. See `--mini-snap`.
      frame.style.setProperty('--mini-snap', '0s');
      // An attribute outlives a `className` exactly as an inline style does, so it is cleared on
      // every reshape too — a frame going to the parking bay wearing a ring would be waiting there
      // with it for the next video, and the bar on the window edge would be waiting in plain sight.
      disarm();
      frame.className = minimized ? FRAME : PARKED;
    },
    follow(drag) {
      /*
       * Cleared rather than written back to identity, and both halves matter. An inline value outlives
       * every `className` this file assigns, so a drag left on the frame would follow it into the
       * parking bay and be waiting there for the next video. And *removing* the properties is what
       * animates the settle: each falls back to the rest value its `var()` names, `--mini-snap` goes
       * with them, and the stylesheet's own transition carries the box home.
       */
      if (drag === null) {
        for (const name of DRAG_PROPERTIES) frame.style.removeProperty(name);
        disarm();
        return;
      }
      frame.style.setProperty('--mini-drag-x', px(drag.dx));
      frame.style.setProperty('--mini-drag-y', px(drag.dy));
      frame.style.setProperty('--mini-grow', String(Math.round(drag.grow * 1e4) / 1e4));
      frame.style.setProperty('--mini-fade', String(Math.round(drag.fade * 100) / 100));
      frame.style.setProperty('--mini-snap', '0s');
      const heading = armedEdge(drag);
      if (heading === null) {
        disarm();
        return;
      }
      frame.dataset[ARMED_ATTRIBUTE] = heading;
      /*
       * The bar is measured and placed only when the edge it is on *changes*, which is what keeps the
       * layout read off the per-move path — and the guard is read off the bar's own attribute rather
       * than from a variable, so the measurement and the thing it describes can never drift apart. A
       * drag that crosses the dominance rule mid-gesture (rightward, then down) genuinely changes
       * edges, and re-measuring is right there too: the new axis is again the one that has not moved.
       */
      const onWindow = windowEdge(heading);
      if (onWindow === null) delete edge.dataset[ARMED_ATTRIBUTE];
      else if (edge.dataset[ARMED_ATTRIBUTE] !== onWindow) {
        aimEdge(onWindow);
        edge.dataset[ARMED_ATTRIBUTE] = onWindow;
      }
    },
  };
}
